import { Box, Flex, FloraTypography, Toaster } from '@grupoboticario/flora-react'

import { processImageUpload, extractFileFromDrop } from '@/utils/imageUpload'

import { IMAGE_UPLOAD_CONSTANTS } from './ImageUpload.constants'
import {
  containerStyle,
  previewBoxStyle,
  previewLabelStyle,
  uploadAreaBoxStyle,
  uploadLabelStyle,
  uploadTextStyle,
  infoTextStyle,
  requiredAsteriskStyle,
  selectFileLinkStyle,
  getPreviewImageStyle,
  overlayTextCss,
  getHoverOverlayCss,
  getUploadFlexCss
} from './ImageUpload.styles'
import { useDragAndDrop } from './useDragAndDrop'

interface ImageUploadProps {
  imagePreview?: string | null
  setImagePreview: (url: string) => void
  getSignedUrl: () => Promise<{ data?: { getEventSignedUrl?: { url?: string; image?: string } } }>
  onImageChange: (imageKey: string | undefined, previewUrl: string | null) => void
}

const ImageUpload = ({ imagePreview = null, setImagePreview, getSignedUrl, onImageChange }: ImageUploadProps) => {
  const { isDragging, handleDragOver, handleDragEnter, handleDragLeave, handleDrop } = useDragAndDrop()

  const handleError = (errorMessage: string) => {
    Toaster.notify({
      kind: 'error',
      description:
        errorMessage === 'Error uploading the image!'
          ? 'Erro ao fazer upload da imagem. Tente novamente.'
          : errorMessage
    })
  }

  const handleFileUpload = async (file: File | null) => {
    if (file) {
      await processImageUpload(
        file,
        getSignedUrl,
        setImagePreview,
        (file, imageKey, previewUrl) => onImageChange(imageKey, previewUrl),
        handleError
      )
    }
  }

  const handleDropWithUpload = (e: DragEvent) => {
    const file = extractFileFromDrop(e)
    handleFileUpload(file)
  }

  const handleInputUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    const file = files && files.length > 0 ? files[0] : null
    await handleFileUpload(file)
  }

  const renderImagePreview = () => (
    <Box css={previewBoxStyle}>
      <Box as="label" css={previewLabelStyle} htmlFor={IMAGE_UPLOAD_CONSTANTS.INPUT_IDS.PREVIEW}>
        {imagePreview ? (
          <img
            alt={IMAGE_UPLOAD_CONSTANTS.ALT_TEXT}
            src={imagePreview}
            style={{
              ...getPreviewImageStyle(IMAGE_UPLOAD_CONSTANTS.DIMENSIONS),
              objectFit: 'cover' as React.CSSProperties['objectFit']
            }}
          />
        ) : (
          <Box>Pré-visualização não disponível</Box>
        )}
        <Box
          className="hover-overlay"
          css={getHoverOverlayCss(IMAGE_UPLOAD_CONSTANTS.DIMENSIONS.WIDTH, IMAGE_UPLOAD_CONSTANTS.DIMENSIONS.HEIGHT)}
        >
          <FloraTypography css={overlayTextCss} fontSize="bodySmallStandard" fontWeight="medium">
            {IMAGE_UPLOAD_CONSTANTS.MESSAGES.CHANGE_IMAGE}
          </FloraTypography>
        </Box>
      </Box>
      <input
        accept={IMAGE_UPLOAD_CONSTANTS.ACCEPTED_TYPES.PREVIEW}
        hidden
        id={IMAGE_UPLOAD_CONSTANTS.INPUT_IDS.PREVIEW}
        onChange={handleInputUpload}
        type="file"
      />
    </Box>
  )

  const renderUploadArea = () => (
    <Box css={uploadAreaBoxStyle}>
      <FloraTypography css={uploadLabelStyle} fontSize="bodyLargeStandard">
        {IMAGE_UPLOAD_CONSTANTS.MESSAGES.REQUIRED_LABEL}{' '}
        <FloraTypography as="span" css={requiredAsteriskStyle} fontSize="bodyLargeStandard">
          *
        </FloraTypography>
      </FloraTypography>
      <Flex
        align="center"
        as="label"
        css={getUploadFlexCss(
          isDragging,
          IMAGE_UPLOAD_CONSTANTS.DIMENSIONS.WIDTH,
          IMAGE_UPLOAD_CONSTANTS.DIMENSIONS.HEIGHT
        )}
        direction="column"
        htmlFor={IMAGE_UPLOAD_CONSTANTS.INPUT_IDS.UPLOAD}
        justify="center"
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={(e) => handleDrop(e, handleDropWithUpload)}
      >
        <FloraTypography css={uploadTextStyle} fontSize="bodyLargeStandard">
          {isDragging ? (
            IMAGE_UPLOAD_CONSTANTS.MESSAGES.DRAG_ACTIVE
          ) : (
            <>
              {IMAGE_UPLOAD_CONSTANTS.MESSAGES.DRAG_DEFAULT}{' '}
              <FloraTypography as="span" css={selectFileLinkStyle} fontSize="bodyLargeStandard">
                {IMAGE_UPLOAD_CONSTANTS.MESSAGES.SELECT_FILE}
              </FloraTypography>
            </>
          )}
        </FloraTypography>
        <input
          accept={IMAGE_UPLOAD_CONSTANTS.ACCEPTED_TYPES.UPLOAD}
          hidden
          id={IMAGE_UPLOAD_CONSTANTS.INPUT_IDS.UPLOAD}
          onChange={handleInputUpload}
          type="file"
        />
      </Flex>
    </Box>
  )

  return (
    <Box css={containerStyle}>
      {imagePreview ? renderImagePreview() : renderUploadArea()}
      <Box css={infoTextStyle}>
        <FloraTypography fontSize="exceptionsAuxiliar">{IMAGE_UPLOAD_CONSTANTS.MESSAGES.SIZE_INFO}</FloraTypography>
      </Box>
    </Box>
  )
}

export default ImageUpload
